/**
 * Cookies module.
 *
 * Uses js-cookie to manage cookies.
 */

import CookiesAPI from 'js-cookie';
import { getDomainFromFQDN, isCypress } from 'scripts/helpers/_utils';
let Cookies;

export const COOKIE_USER_COUNTRY_CODE = 'user-country-code';
export const COOKIE_USER_COUNTRY_NAME = 'user-country-name';
export const COOKIE_USER_LOCATION_COUNTRY_CODE = 'user-location-country-code';
export const COOKIE_USER_CURRENCY_CODE = 'user-currency-code';

/**
 * Set up cookie default parameters.
 *
 * @param {Object} [options={}] Options for setting default parameters. Supports `path` and `domain` params.
 */
export const initCookieDefaults = ( options = {} ) => {
	const defaults = {};
	if ( 'string' === typeof options.path ) {
		defaults.path = options.path;
	} else {
		defaults.path = '/';
	}

	if ( 'string' === typeof options.domain ) {
		defaults.domain = options.domain;
	} else if ( 'function' === typeof getDomainFromFQDN ) {
		defaults.domain = getDomainFromFQDN( window.location.host );
	} else {
		defaults.domain = window.location.host;
	}

	Cookies = CookiesAPI.withAttributes( defaults );
};

/**
 * Sets a cookie value using the site defaults (path = /; domain = parent domain).
 *
 * @param {string} name         Name of the cookie to be set.
 * @param {string} value        Cookie value to be stored.
 * @param {number} days         Number of days to set the cookie for.
 * @param {Object} [options={}] Full options object for js-cookie if needed.
 */
export const createCookie = ( name, value, days, options = {} ) => {
	if ( ! Cookies ) {
		initCookieDefaults();
	}

	if ( days ) {
		options.expires = days;
	}

	Cookies.set( name, value, options );
};

/**
 * Gets a cookie value by name.
 *
 * @param {string} name Name of the cookie to retrieve.
 * @return {string} Cookie value.
 */
export const readCookie = ( name ) => {
	if ( ! Cookies ) {
		initCookieDefaults();
	}

	return Cookies.get( name );
};

/**
 * Removes a cookie by name.
 *
 * @param {string} name         Name of the cookie to remove.
 * @param {Object} [options={}] Full options object for js-cookie if needed.
 */
export const eraseCookie = ( name, options = {} ) => {
	if ( ! Cookies ) {
		initCookieDefaults();
	}

	Cookies.remove( name, options );
};

/**
 * If a cookie is not already set, sets a cookie value using the site defaults (path = /; domain = parent domain).
 *
 * @param {string} name         Name of the cookie to be set.
 * @param {string} value        Cookie value to be stored.
 * @param {number} days         Number of days to set the cookie for.
 * @param {Object} [options={}] Full options object for js-cookie if needed.
 */
export const createCookieIfMissing = ( name, value, days = null, options = {} ) => {
	if ( typeof readCookie( name ) === 'undefined' ) {
		createCookie( name, value, days, options );
	}
};

/**
 * Is the cookie policy explicitly accepted.
 *
 * @return {boolean} Cookie policy accepted.
 */
export const isCookiePolicyAccepted = () => {
	// eslint-disable-next-line no-undef
	if ( typeof Osano !== 'undefined' && typeof Osano.cm !== 'undefined' ) {
		// eslint-disable-next-line no-undef
		const consent = Osano.cm.getConsent();

		return consent && consent?.ESSENTIAL === 'ACCEPT';
	}

	return isCypress();
};

/**
 * Is the cookie policy explicitly declined.
 *
 * @return {boolean} Cookie policy declined.
 */
export const isCookiePolicyDeclined = () => {
	// eslint-disable-next-line no-undef
	if ( typeof Osano !== 'undefined' && typeof Osano.cm !== 'undefined' ) {
		// eslint-disable-next-line no-undef
		const consent = Osano.cm.getConsent();

		return consent && consent?.ESSENTIAL !== 'ACCEPT';
	}

	return false;
};
